import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../components/Icon.tsx';
import { useRecorder } from './useRecorder.ts';
import { Waveform } from './Waveform.tsx';

// Local Dhivehi strings — kept inline (not in i18n/dv.ts) since dv.ts carries
// unrelated in-flight edits on this branch and this task's commit must stay
// scoped to RecordingBar.tsx + YaumiyyaEditor.tsx.
const STR = {
  consentNotice: 'މި ބައްދަލުވުން ރެކޯޑް ކުރެވެނީ',
  requesting: 'މައިކްރޮފޯނުގެ ހުއްދައަށް އެދެނީ…',
  processing: 'ޕްރޮސެސްކުރަނީ…',
  pause: 'މެދުކަނޑާލާ',
  resume: 'ކުރިއަށް ގެންދޭ',
  stop: 'ހުއްޓާލާ',
  dismiss: 'ބަންދުކުރޭ',
};

// HH:MM:SS from an elapsed-ms count. Pure helper, hours can exceed 24 (kept
// unpadded like a stopwatch would) — meetings shouldn't run that long, but a
// clock display should never wrap either way.
export function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// Human-readable file size from bytes. Returns e.g. "5.3 MB", "842 KB", or "—" for null.
export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIdx = 0;
  while (size >= 1024 && unitIdx < units.length - 1) {
    size /= 1024;
    unitIdx++;
  }
  return `${size.toFixed(1)} ${units[unitIdx]}`;
}

// Runs `effect` once on mount only — guards useRecorder's auto-start against
// React StrictMode's dev-mode double-invoke of effects.
function useEffectOnce(effect: () => void): void {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// Persistent recording bar, fixed to the bottom of the viewport. Rendered via
// a portal to document.body so editor scroll (or any ancestor's) never moves
// it — it must stay visible while the minute-taker scrolls the long form.
export function RecordingBar({ meetingId, onClosed }: { meetingId: string; onClosed?: () => void }): JSX.Element | null {
  const { state, elapsedMs, analyser, error, start, pause, resume, stop } = useRecorder(meetingId);

  // Auto-start on mount (kept simple per the brief: the bar owns start()).
  useEffectOnce(() => { void start(); });

  const recording = state === 'recording';
  const paused = state === 'paused';

  // useRecorder never transitions itself out of 'processing' (it's the
  // terminal success state — finalizeRecording already happened by the time
  // stop() resolves), so "settles" here means the stop() promise resolving.
  // stop() never rejects — on a flush/finalize failure it resolves with
  // 'error' instead. Only hand control back to the editor on success; on
  // 'error' keep the bar mounted so the error message + its dismiss button
  // (which itself calls onClosed) are visible to the user.
  async function handleStop(): Promise<void> {
    const settled = await stop();
    if (settled === 'error') return;
    onClosed?.();
  }

  if (state === 'idle') return null;

  return createPortal(
    <div
      dir="rtl"
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-[hsl(var(--border))] bg-[hsl(var(--chrome))] shadow-[0_-2px_12px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="page flex flex-wrap items-center gap-3 py-2.5">
        {state === 'requesting' && (
          <div className="flex flex-1 items-center gap-2 text-sm text-muted-foreground">
            <Icon name="mic" size={18} className="shrink-0" />
            <span>{STR.requesting}</span>
          </div>
        )}

        {(recording || paused) && (
          <>
            <span
              aria-hidden
              className={`size-2.5 shrink-0 rounded-full bg-destructive ${recording ? 'animate-pulse' : 'opacity-40'}`}
            />
            <Waveform analyser={analyser} active={recording} paused={paused} className="h-8 w-24 shrink-0 sm:w-40" />
            <span dir="ltr" className="mono-meta shrink-0 text-sm tabular-nums">{formatElapsed(elapsedMs)}</span>
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{STR.consentNotice}</span>
            <button type="button" className="btn-secondary btn-sm shrink-0 inline-flex items-center gap-1.5" onClick={() => (recording ? pause() : resume())}>
              <Icon name={recording ? 'pause' : 'play_arrow'} size={16} />
              {recording ? STR.pause : STR.resume}
            </button>
            <button type="button" className="btn-danger btn-sm shrink-0 inline-flex items-center gap-1.5" onClick={() => void handleStop()}>
              <Icon name="stop" size={16} />
              {STR.stop}
            </button>
          </>
        )}

        {state === 'processing' && (
          <div className="flex flex-1 items-center gap-2 text-sm text-muted-foreground">
            <span
              aria-hidden
              className="size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
            />
            <span>{STR.processing}</span>
          </div>
        )}

        {state === 'error' && (
          <>
            <span className="min-w-0 flex-1 truncate text-sm text-destructive">{error ?? STR.processing}</span>
            <button type="button" aria-label={STR.dismiss} className="grid size-11 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted/70" onClick={() => onClosed?.()}>
              <Icon name="close" size={20} />
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
